import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaEdit, FaTrash } from "react-icons/fa";
import InquiryDeleteModal from "../../components/Modals/InquiryDeleteModel";
import NewlyInquiries from "../../components/auth/Inquiry/NewlyInquiries";
import ReservedInquiries from "../../components/auth/Inquiry/ReservedInquiries";
import RejectInquiry from "../../components/auth/Inquiry/RejectInquiry";

const Inquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [activeTab, setActiveTab] = useState("Newly Inquiries");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/form/get-all-inquiries`
      );

      const inquiriesData = Array.isArray(response.data)
        ? response.data
        : response.data.inquiries
        ? response.data.inquiries
        : response.data.data
        ? response.data.data
        : response.data.inquiry
        ? [response.data.inquiry]
        : [];

      setInquiries(inquiriesData);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      setInquiries([]);
      showMessage("Failed to fetch inquiries", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (inq) => {
    setSelectedInquiry(inq);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedInquiry) return;

    try {
      setDeleteLoading(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/form/delete-inquiry/${selectedInquiry._id}`
      );

      console.log("Delete response:", response.data);

      if (response.data.success) {
        // Remove the deleted inquiry from the state
        setInquiries(
          inquiries.filter((inq) => inq._id !== selectedInquiry._id)
        );
        showMessage("Inquiry deleted successfully", "success");
      } else {
        showMessage("Failed to delete inquiry", "error");
      }
    } catch (error) {
      console.error("Error deleting inquiry:", error);

      // More detailed error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
        showMessage(
          `Error: ${error.response.data.message || "Failed to delete inquiry"}`,
          "error"
        );
      } else if (error.request) {
        // The request was made but no response was received
        console.error("Request data:", error.request);
        showMessage("No response received from server", "error");
      } else {
        // Something happened in setting up the request
        console.error("Error message:", error.message);
        showMessage(`Error: ${error.message}`, "error");
      }
    } finally {
      setDeleteLoading(false);
      setIsModalOpen(false);
      setSelectedInquiry(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInquiry(null);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 5000);
  };

  if (loading) {
    return (
      <p className="text-center py-8 sm:py-10 text-gray-500 text-sm sm:text-base">
        Loading inquiries...
      </p>
    );
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setQuery("");
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Newly Inquiries":
        return <NewlyInquiries />;
      case "Interested":
        return (
          <ReservedInquiries
            inquiries={inquiries.filter((i) => i.status === "reserved")}
          />
        );
      case "Not Interested":
        return (
          <RejectInquiry
            inquiries={inquiries.filter((i) => i.status === "rejected")}
          />
        );
      default:
        return null;
    }
  };

  // change status
  const updateStatus = async (inq, newStatus) => {
    try {
      // API call to update inquiry status
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/form/update-inquiry/${inq._id}`,
        { status: newStatus }
      );

      if (response.data.success) {
        // Update state locally
        setInquiries((prev) =>
          prev.map((item) =>
            item._id === inq._id ? { ...item, status: newStatus } : item
          )
        );
        showMessage(`Inquiry moved to ${newStatus}`, "success");
      } else {
        showMessage("Failed to update status", "error");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showMessage("Error updating status", "error");
    }
  };

  return (
    <div className="py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-[#0070a8] text-xl sm:text-2xl lg:text-3xl font-bold text-left">
          Enquiries
        </h1>
      </div>

      {/* Tabs */}
      <div className="px-2 sm:px-4 lg:px-6">
        <div className="border-b tracking-wide">
          <div className="flex justify-center overflow-x-auto">
            <div className="flex space-x-1 sm:space-x-2 md:space-x-4 min-w-max">
              {["Newly Inquiries", "Interested", "Not Interested"].map(
                (tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`px-3 sm:px-4 lg:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-colors duration-200 ${
                      activeTab === tab
                        ? "border-primaryColor text-primaryColor bg-purple-50"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto mt-4 sm:mt-6 px-2 sm:px-4">
        {renderTabContent()}
      </div>

      {/* Message Display */}
      {message.text && (
        <div
          className={`mt-4 sm:mt-6 mx-2 sm:mx-4 p-3 sm:p-4 rounded-md text-sm sm:text-base ${
            message.type === "success"
              ? "bg-green-100 text-green-800 border border-green-400"
              : "bg-red-100 text-red-800 border border-red-400"
          }`}
        >
          {message.text}
        </div>
      )}

      <InquiryDeleteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Inquiry"
        message="Are you sure you want to delete this inquiry? This action cannot be undone."
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default Inquiries;
